export const BUSINESS_HOUR_MS = 3_600_000;
export const IST_OFFSET_MINUTES = 330; // Asia/Kolkata, fixed (no DST)

export const SLA_CALENDAR = {
  workDays: [1, 2, 3, 4, 5], // Mon–Fri (IST weekday: 0=Sun..6=Sat)
  windowStartHour: 10, // 10:00 IST
  windowEndHour: 19, // 19:00 IST (9 business hours/day)
  holidays: [
    "2026-01-26",
    "2026-03-04",
    "2026-03-21",
    "2026-03-26",
    "2026-08-15",
    "2026-08-20",
    "2026-10-02",
    "2026-10-20",
    "2026-11-08",
    "2026-11-09",
  ] as string[],
};
export type SlaCalendar = typeof SLA_CALENDAR;

const HOUR_MS = 3_600_000;
const IST_OFFSET_MS = IST_OFFSET_MINUTES * 60_000;

function toIst(ms: number): Date {
  return new Date(ms + IST_OFFSET_MS);
}

function istDateStr(ms: number): string {
  const d = toIst(ms);
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${mo}-${day}`;
}

export function isHoliday(
  ms: number,
  cal: SlaCalendar = SLA_CALENDAR,
): boolean {
  return cal.holidays.includes(istDateStr(ms));
}

export function isWorkingDay(
  ms: number,
  cal: SlaCalendar = SLA_CALENDAR,
): boolean {
  return cal.workDays.includes(toIst(ms).getUTCDay()) && !isHoliday(ms, cal);
}

function windowBounds(anchorMs: number, cal: SlaCalendar) {
  const d = toIst(anchorMs);
  const istMidnightEpoch =
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) -
    IST_OFFSET_MS;
  return {
    start: istMidnightEpoch + cal.windowStartHour * HOUR_MS,
    end: istMidnightEpoch + cal.windowEndHour * HOUR_MS,
  };
}

function nextIstMidnight(anchorMs: number): number {
  const d = toIst(anchorMs);
  return (
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) -
    IST_OFFSET_MS
  );
}

export function businessTimeBetween(
  startMs: number,
  endMs: number,
  cal: SlaCalendar = SLA_CALENDAR,
): number {
  if (endMs <= startMs) return 0;
  let total = 0;
  let cursor = startMs;
  let guard = 0;
  while (cursor < endMs && guard++ < 4000) {
    if (isWorkingDay(cursor, cal)) {
      const { start, end } = windowBounds(cursor, cal);
      const segStart = Math.max(startMs, start);
      const segEnd = Math.min(endMs, end);
      if (segEnd > segStart) total += segEnd - segStart;
    }
    cursor = nextIstMidnight(cursor);
  }
  return total;
}

export function addBusinessTime(
  startMs: number,
  durationMs: number,
  cal: SlaCalendar = SLA_CALENDAR,
): number {
  if (durationMs <= 0) return startMs;
  let remaining = durationMs;
  let cursor = startMs;
  let guard = 0;
  while (remaining > 0 && guard++ < 4000) {
    if (isWorkingDay(cursor, cal)) {
      const { start, end } = windowBounds(cursor, cal);
      const segStart = Math.max(cursor, start);
      if (segStart < end) {
        const available = end - segStart;
        if (available >= remaining) return segStart + remaining;
        remaining -= available;
      }
    }
    cursor = nextIstMidnight(cursor);
  }
  return cursor;
}
